import React, { useState } from 'react';
import apiClient from '../api/client';
import { 
  Send, 
  MessageSquare, 
  Mail, 
  User, 
  Star,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface FeedbackForm {
  name: string;
  email: string;
  subject: string;
  message: string;
  rating: number;
  category: string;
}

const Feedback: React.FC = () => {
  const [form, setForm] = useState<FeedbackForm>({
    name: '',
    email: '',
    subject: '',
    message: '',
    rating: 0,
    category: 'general'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = [
    { value: 'general', label: 'Genel Geri Bildirim' },
    { value: 'bug', label: 'Hata Bildirimi' },
    { value: 'feature', label: 'Özellik Önerisi' },
    { value: 'ui', label: 'Arayüz Önerisi' },
    { value: 'performance', label: 'Performans' },
    { value: 'other', label: 'Diğer' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingClick = (rating: number) => {
    setForm(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post('/feedback', {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        rating: form.rating,
        category: form.category
      });

      if (response.data.success) {
        setIsSubmitted(true);
      } else {
        setError(response.data.error?.message || 'Geri bildirim gönderilirken bir hata oluştu.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Geri bildirim gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      subject: '',
      message: '',
      rating: 0,
      category: 'general'
    });
    setIsSubmitted(false);
    setError(null);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Geri Bildiriminiz Alındı!
            </h1>
            
            <p className="text-lg text-gray-600 mb-6">
              Değerli geri bildiriminiz için teşekkür ederiz. 
              Mesajınız başarıyla <strong>info@avukatech.com</strong> adresine gönderildi.
            </p>
            
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Not:</strong> Geri bildiriminiz en kısa sürede değerlendirilecek ve 
                gerekirse size dönüş yapılacaktır.
              </p>
            </div>
            
            <div className="flex gap-4 justify-center">
              <button
                onClick={resetForm}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Yeni Geri Bildirim
              </button>
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Dashboard'a Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Geri Bildirim Gönder
            </h1>
            <p className="text-gray-600">
              Görüşleriniz bizim için çok değerli. Deneyiminizi bizimle paylaşın.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Ad Soyad
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Adınız ve soyadınız"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Kategori
              </label>
              <select
                id="category"
                name="category"
                value={form.category}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Konu
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={form.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Geri bildiriminizin kısa özeti"
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Star className="w-4 h-4 inline mr-1" />
                Genel Değerlendirme
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingClick(star)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      star <= form.rating
                        ? 'bg-yellow-400 text-yellow-900'
                        : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                    }`}
                  >
                    <Star className="w-5 h-5 fill-current" />
                  </button>
                ))}
              </div>
              {form.rating > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {form.rating}/5 yıldız - {
                    form.rating === 1 ? 'Çok Kötü' :
                    form.rating === 2 ? 'Kötü' :
                    form.rating === 3 ? 'Orta' :
                    form.rating === 4 ? 'İyi' : 'Mükemmel'
                  }
                </p>
              )}
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Mesaj
              </label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Geri bildiriminizi detaylı olarak yazın..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full flex items-center justify-center px-6 py-4 rounded-lg font-medium transition-colors ${
                isSubmitting
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Hazırlanıyor...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Geri Bildirim Gönder
                </>
              )}
            </button>
          </form>

          {/* Info */}
          <div className="mt-8 bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Nasıl Çalışır?</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Formu doldurduktan sonra "Geri Bildirim Gönder" butonuna tıklayın</li>
              <li>• Mesajınız otomatik olarak <strong>info@avukatech.com</strong> adresine gönderilir</li>
              <li>• Geri bildiriminiz en kısa sürede değerlendirilecektir</li>
              <li>• Gerekirse size email ile dönüş yapılacaktır</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
